import { useLayoutEffect, useState, type ReactNode } from 'react'
import { useSettingsStore } from '../stores/useSettingsStore'
import { applyDeviceTheme, writeDeviceTheme } from '../logic/deviceTheme'

/** Mount only once configuration is resolved; descendants never render a guessed theme. */
export default function ThemeController({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)

  useLayoutEffect(() => {
    const root = document.documentElement
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)')
    const applyTheme = () => {
      const { themeSetting, configurationLoaded } = useSettingsStore.getState()
      const resolved =
        themeSetting === 'system' ? (mediaQuery?.matches ? 'dark' : 'light') : themeSetting

      root.dataset.themeSetting = themeSetting
      root.dataset.themeResolved = resolved
      root.dataset.theme = resolved
      root.style.colorScheme = resolved
      // A session's own theme becomes this device's startup theme; signed-out
      // pages only show the device's, so they never replace it.
      if (configurationLoaded) writeDeviceTheme(themeSetting)
    }

    // Apply synchronously before mounting children, and keep later preference
    // edits / OS changes synchronized without a corrective React render.
    const unsubscribe = useSettingsStore.subscribe((state, previous) => {
      if (state.themeSetting !== previous.themeSetting) applyTheme()
    })
    mediaQuery?.addEventListener('change', applyTheme)
    applyTheme()
    setReady(true)

    return () => {
      unsubscribe()
      mediaQuery?.removeEventListener('change', applyTheme)
      // A new identity resolves its own theme behind the startup boundary;
      // until then the screen keeps the device's theme instead of going white.
      applyDeviceTheme()
    }
  }, [])

  return ready ? children : null
}
